import { ColorNeutralGrey3, Spacing16, Spacing8 } from 'autogen/design-tokens/tokens';
import type { VariantDisplayType, VariantSectionModel } from 'autogen/swagger/all';
import { ShowMoreVariantsButton } from 'autogen/translation-keys/trans-website-product-productvariants';
import Icon from 'components/Icon';
import ScrollSnapSlider from 'components/generic/ScrollSnapSlider';
import SwatchRenderer from 'components/product/Variants/Swatches/SwatchRenderer';
import { DisplayTypeSize, swatchStateStyling } from 'components/product/Variants/Swatches/shared/styles';
import WhenDesktop from 'components/shared/inputs/select/components/WhenDesktop';
import useHiddenItemsObserver from 'hooks/globals/useHiddenItemsObserver';
import { removeButtonStyling, removeListStyling } from 'modules/helpers/mixins';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { breakpoint, sizes } from 'variables';
import SectionHeader from './SectionHeader';

// #region Styles
const gapValue = Spacing8;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing8};
`;

const Nav = styled.nav``;

const List = styled(ScrollSnapSlider)<{
    $displayType: VariantDisplayType;
    $truncate: boolean;
    $listMaxHeight?: number;
}>`
    // && - prevent styling being overridden by ScrollSnapSlider
    && {
        ${removeListStyling}

        gap: ${gapValue};

        margin-left: -${Spacing16};
        margin-right: -${Spacing16};
        padding-left: ${Spacing16};
        padding-right: ${Spacing16};
        scroll-padding-inline-start: ${Spacing16};
        scroll-padding-inline-end: ${Spacing16};

        ${breakpoint.up(sizes.md)} {
            margin: 0;
            padding: 0;
            gap: ${Spacing8};
            width: auto;
            transition: max-height 200ms ease;

            ${({ $displayType }) =>
                $displayType === 'Text'
                    ? css`
                          display: flex;
                          flex-wrap: wrap;
                      `
                    : css`
                          display: grid;
                          grid-template-columns: repeat(
                              auto-fill,
                              minmax(min-content, ${DisplayTypeSize[$displayType].width})
                          );
                      `}

            ${({ $displayType, $truncate, $listMaxHeight }) =>
                $truncate
                    ? css`
                          max-height: calc(${DisplayTypeSize[$displayType].height} * 2 + ${gapValue});
                          overflow: hidden;
                      `
                    : css`
                          ${
                              $listMaxHeight &&
                              css`
                              max-height: ${$listMaxHeight}px;
                          `
                          }
                      `}
        }
    }
`;

const ListItem = styled.li<{ $hide: boolean; $order: number }>`
    ${breakpoint.up(sizes.md)} {
        ${({ $order }) =>
            $order &&
            css`
                order: ${$order};
            `}

        ${({ $hide }) =>
            $hide &&
            css`
                opacity: 0;
            `}
    }
`;

const ToggleButton = styled.button<{ $truncate: boolean; $order: number; $displayType: VariantDisplayType }>`
    display: none;

    ${breakpoint.up(sizes.md)} {
        ${removeButtonStyling}
        ${swatchStateStyling({ $size: 'lg' })}

        display: flex;
        justify-content: center;
        align-items: center;
        outline: 1px solid ${ColorNeutralGrey3};
        outline-offset: -1px;
        padding: 0 ${Spacing8};

        ${({ $displayType }) =>
            $displayType === 'Text' &&
            css`
                min-width: 60px;
                min-height: 40px;
            `}

        ${({ $truncate }) =>
            $truncate &&
            css`
                grid-column: -2;
                grid-row: 2;
            `}

        ${({ $order }) =>
            $order &&
            css`
                order: ${$order};
            `}
    }
`;

const StyledSectionHeader = styled(SectionHeader)`
    white-space: normal;
`;

// #endregion Styles

export default function VariantsSection({
    variantsSection,
    hasMultipleVariantSections,
    ...restProps
}: {
    variantsSection: VariantSectionModel;
    hasMultipleVariantSections: boolean;
}) {
    const { variants, variantPickerButtonText } = variantsSection;
    const { displayType, productId } = variants[0];
    const selectedItem = variants.find((variant) => variant.isSelected);

    const [truncate, setTruncate] = useState(true);
    const [listMaxHeight, setListMaxHeight] = useState(undefined);
    const ref = useRef<any>(null);
    const hiddenItems = useHiddenItemsObserver(ref); // -1, because we are not counting the truncate button
    const [lastVisibleItem, setLastVisibleItem] = useState(variants?.length - hiddenItems);
    const { t } = useTranslation();
    const [focusedItem, setFocusedItem] = useState(selectedItem);

    function collapse(value: boolean) {
        setTruncate(value);
        setListMaxHeight(ref.current.scrollHeight);
    }

    useEffect(() => {
        setLastVisibleItem(variants?.length - hiddenItems);
    }, [hiddenItems, variants]);

    useEffect(() => {
        if (truncate === false) {
            setLastVisibleItem(variants?.length);
        }
    }, [truncate]);

    return (
        <Wrapper {...restProps}>
            {selectedItem && (
                <StyledSectionHeader
                    {...focusedItem}
                    productId={productId}
                    variantPickerButtonText={variantPickerButtonText}
                />
            )}

            {(variants.length > 1 || hasMultipleVariantSections) && (
                <Nav>
                    <List
                        forwardedAs="ul"
                        ref={ref}
                        $displayType={displayType}
                        $truncate={truncate}
                        $listMaxHeight={listMaxHeight}
                    >
                        {variants.map((variant, index) => {
                            let order;
                            let hide = false;
                            if (displayType === 'Text' && hiddenItems > 0) {
                                const incrementedIndex = index + 1;
                                const isAfterLastVisibleItem = incrementedIndex >= lastVisibleItem;
                                order = isAfterLastVisibleItem ? incrementedIndex + 1 : incrementedIndex;
                                hide = isAfterLastVisibleItem;
                            }

                            return (
                                <ListItem key={variant.productId} $hide={hide} $order={order}>
                                    <SwatchRenderer
                                        {...variant}
                                        onMouseOver={() => {
                                            setFocusedItem(variant);
                                        }}
                                        onMouseLeave={() => {
                                            setFocusedItem(selectedItem);
                                        }}
                                        onFocus={() => {
                                            setFocusedItem(variant);
                                        }}
                                        onBlur={() => {
                                            setFocusedItem(selectedItem);
                                        }}
                                    />
                                </ListItem>
                            );
                        })}

                        <WhenDesktop>
                            {hiddenItems > 0 && truncate && (
                                <ToggleButton
                                    $order={lastVisibleItem}
                                    $truncate={truncate}
                                    $displayType={displayType}
                                    onClick={() => {
                                        collapse(false);
                                    }}
                                >
                                    {displayType === 'Text' ? t(ShowMoreVariantsButton) : <>+{hiddenItems}</>}
                                </ToggleButton>
                            )}

                            {!truncate && (
                                <ToggleButton
                                    $order={lastVisibleItem}
                                    $truncate={truncate}
                                    $displayType={displayType}
                                    onClick={() => {
                                        collapse(true);
                                    }}
                                >
                                    <Icon type="ChevronUp" size={16} />
                                </ToggleButton>
                            )}
                        </WhenDesktop>
                    </List>
                </Nav>
            )}
        </Wrapper>
    );
}
